import { Form } from 'semantic-ui-react';
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';

import { getUserById, updateAdminUserById } from '~/api/admin';
import { Button, Modal } from '~/components';

const ROLE_LABEL: Record<string, string> = {
  ADMIN: 'Admin',
  INVESTOR: 'Investor',
  INSTALLER: 'Developer',
  EXTERNAL: 'External',
};

interface EditAdminUserModalProps {
  userId: string;
  closeModal: () => void;
  /** Called after a successful save (e.g. invalidate company team queries). */
  onUserSaved?: () => void;
}

export default function EditAdminUserModal({ userId, closeModal, onUserSaved }: EditAdminUserModalProps) {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const { data: detail, isLoading: detailLoading, isError } = useQuery(
    ['adminUserDetail', userId],
    () => getUserById(userId),
    { enabled: Boolean(userId) }
  );

  useEffect(() => {
    if (!detail) {
      return;
    }
    setName(detail.name ?? '');
    setEmail(detail.email ?? '');
    setPassword('');
  }, [detail]);

  const saveMutation = useMutation(
    () =>
      updateAdminUserById(userId, {
        name: name.trim(),
        email: email.trim(),
        ...(password.trim() ? { password: password.trim() } : {}),
      }),
    {
      onSuccess: () => {
        toast.success('User updated.');
        queryClient.invalidateQueries({ queryKey: ['adminUsersPage'] });
        onUserSaved?.();
        closeModal();
      },
      onError: (e: unknown) => {
        console.error(e);
        const msg =
          typeof e === 'object' && e != null && 'response' in e
            ? (e as { response?: { data?: { message?: string } } }).response?.data?.message
            : undefined;
        toast.error(msg || 'Could not update user. Please try again.');
      },
    }
  );

  const handleClose = () => {
    if (!saveMutation.isLoading) {
      closeModal();
    }
  };

  const handleSubmit = async () => {
    const n = name.trim();
    const em = email.trim();
    if (!n) {
      toast.error('Name is required.');
      return;
    }
    if (!em) {
      toast.error('Email is required.');
      return;
    }
    if (password.trim() && password.trim().length < 8) {
      toast.error('Password must be at least 8 characters.');
      return;
    }
    await saveMutation.mutateAsync();
  };

  const roleLabel = detail ? ROLE_LABEL[detail.roleCode] ?? detail.roleCode : '';

  return (
    <Modal closeModal={handleClose} width='36rem'>
      <Modal.Title>Edit user{roleLabel ? ` · ${roleLabel}` : ''}</Modal.Title>
      <Modal.Content>
        {detailLoading && (
          <p style={{ marginBottom: '1rem' }}>Loading…</p>
        )}
        {isError && (
          <p style={{ color: '#9f3a38', marginBottom: '1rem' }}>Could not load user details.</p>
        )}
        {detail && (
          <Form id='edit-admin-user-form' onSubmit={e => e.preventDefault()}>
            <Form.Field required>
              <label>Name</label>
              <Form.Input
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder='Full name'
                autoFocus
              />
            </Form.Field>
            <Form.Field required>
              <label>Email</label>
              <Form.Input
                type='email'
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder='email@example.com'
              />
            </Form.Field>
            <Form.Field>
              <label>New password</label>
              <Form.Input
                type='password'
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder='Leave blank to keep current password'
                autoComplete='new-password'
              />
            </Form.Field>
          </Form>
        )}
      </Modal.Content>
      <Modal.Footer>
        <Button primary outlined onClick={handleClose} disabled={saveMutation.isLoading}>
          Cancel
        </Button>
        <Button
          primary
          loading={saveMutation.isLoading}
          disabled={saveMutation.isLoading || detailLoading || !detail}
          onClick={handleSubmit}
        >
          Save
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
